<template>
  <v-navigation-drawer 
    :location="$vuetify.display.mobile ? 'bottom' : 'left'" 
    class="modern-drawer"
    width="320"
  >
    <div class="pa-4">
      <!-- Loading State -->
      <v-card class="modern-card mb-4" elevation="1" v-if="teams.length == 0">
        <v-card-text class="pa-4 text-center">
          <v-progress-linear
            :active="true"
            color="primary"
            height="3"
            indeterminate
            class="mb-3"
          ></v-progress-linear>
          <div class="text-body-2">Loading Teams...</div>
        </v-card-text>
      </v-card>

      <!-- Team Selection -->
      <v-card class="modern-card mb-4" elevation="1" v-if="teams.length > 0">
        <v-card-title class="card-header">
          <v-icon class="me-3" color="primary">mdi-account-group</v-icon>
          Team Selection
        </v-card-title>
        <v-card-text class="pa-4">
          <v-select 
            v-if="teams.length > 1" 
            label="Team" 
            v-model="team" 
            item-title="name" 
            item-value="name" 
            return-object 
            :items="this.teams"
            variant="outlined"
            density="comfortable"
          ></v-select>
          <div v-if="teams.length === 1 && team" class="team-display">
            <v-icon class="me-2" color="success">mdi-account-group</v-icon>
            <span class="text-h6">{{team.name}}</span>
          </div>
        </v-card-text>
      </v-card>

      <!-- Drill Library Tree -->
      <v-card class="modern-card drill-library-card" elevation="1" v-if="team">
        <v-card-title class="card-header">
          <v-icon class="me-3" color="info">mdi-library</v-icon>
          Drill Library
        </v-card-title>
        <div class="drill-library-scroll">
          <DrillLibraryTree :team="this.team" @drillSelected="selectDrill"/>
        </div>
      </v-card>
    </div>
  </v-navigation-drawer>

  <v-main class="drill-library-main">
    <v-container class="pa-6">
      <!-- No Drill Selected State -->
      <v-card class="modern-card" elevation="1" v-if="!drill">
        <v-card-text class="pa-8 text-center">
          <v-icon size="64" color="grey-lighten-1" class="mb-4">mdi-library</v-icon>
          <h3 class="text-h5 mb-2">Select a Drill</h3>
          <p class="text-body-1 text-medium-emphasis">
            Choose a drill from the library to view and edit its details
          </p>
        </v-card-text>
      </v-card>

      <!-- Drill Editor -->
      <v-card class="modern-card" elevation="1" v-if="drill">
        <v-card-title class="card-header">
          <v-icon class="me-3" color="success">mdi-soccer</v-icon>
          Edit Drill
        </v-card-title>
        <v-card-text class="pa-6">
          <!-- Action Buttons -->
          <div class="d-flex gap-3 mb-6">
            <v-btn 
              @click="save" 
              color="success" 
              variant="elevated"
              class="modern-btn"
            >
              <v-icon class="me-2">mdi-content-save</v-icon>
              Save Drill
            </v-btn>
            <v-btn 
              @click="deleteDrill" 
              color="error" 
              variant="outlined"
              class="modern-btn"
            >
              <v-icon class="me-2">mdi-delete</v-icon>
              Delete Drill
            </v-btn>
          </div>

          <!-- Drill Form -->
          <v-row>
            <v-col cols="12" md="8">
              <v-text-field 
                label="Drill Name" 
                v-model="drill.name" 
                variant="outlined"
                density="comfortable"
                class="mb-4"
              ></v-text-field>
            </v-col>
            <v-col cols="12" md="4">
              <v-tooltip text="Add Image">
                <template v-slot:activator="{ props }">
                  <v-btn 
                    v-bind="props" 
                    color="info"
                    variant="outlined"
                    @click="showImageAddDialog()"
                    class="modern-btn"
                    style="width: 100%"
                  >
                    <v-icon class="me-2">mdi-image-plus</v-icon>
                    Add Image
                  </v-btn>
                </template>
              </v-tooltip>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12" md="6">
              <v-select 
                clearable 
                chips 
                label="Groups"  
                v-model="drill.groups" 
                :items="team.groups" 
                multiple 
                variant="outlined"
                density="comfortable"
                class="mb-4"
              ></v-select>
            </v-col>
            <v-col cols="12" md="6">
              <v-select 
                clearable 
                chips 
                label="Tags"  
                v-model="drill.tags" 
                :items="tags" 
                multiple 
                variant="outlined"
                density="comfortable"
                class="mb-4"
              ></v-select>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12">
              <v-textarea 
                label="Notes" 
                auto-grow
                rows="3" 
                v-model="drill.notes" 
                variant="outlined"
                density="comfortable"
                class="mb-4"
              ></v-textarea>
            </v-col>
          </v-row>

          <!-- Images Section -->
          <v-row v-if="drill.images && drill.images.length > 0">
            <v-col cols="12">
              <h4 class="text-h6 mb-4">
                <v-icon class="me-2" color="info">mdi-image</v-icon>
                Images
              </h4>
              <v-row>
                <v-col cols="12" sm="6" md="4" v-for="(image, imageIndex) in drill.images" :key="image">
                  <v-card class="image-card" elevation="1">
                    <ImageDisplay :teamId="team.id" :fileName="image"></ImageDisplay>
                    <v-card-actions class="pa-2">
                      <v-btn 
                        @click="removeDrillImage(imageIndex)"
                        color="error"
                        variant="outlined"
                        size="small"
                        class="action-btn"
                      >
                        <v-icon size="small">mdi-delete</v-icon>
                      </v-btn>
                    </v-card-actions>
                  </v-card>
                </v-col>
              </v-row>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </v-container>
  </v-main>

  <v-container class="outer-container" v-show="false">
    <v-row no-gutters>
      <v-col cols="12" sm="4">
       Drill Library

        <v-container height="auto" style="display:flex">
          <v-row>
            <v-col cols="12" sm="11">
              <v-select v-if="teams.length > 1 " label="Team" v-model="team" item-title="name" item-value="name" return-object :items="this.teams"></v-select>
              <h1 v-if="teams.length === 1 && team">{{team.name}}</h1>
            </v-col>
          </v-row>
          <v-row v-if="team" style="">
            <v-col cols="12" sm="11">
              <v-select v-model="drill"  label="Drill" item-title="name" item-value="name" return-object :items="filteredLibraryDrills">
                <template v-slot:prepend-item>
                <v-list-item>
                  <v-list-item-content>
                    <v-text-field v-model="libraryFilter" ></v-text-field>
                  </v-list-item-content>
                </v-list-item>
                </template>

              </v-select>
            </v-col>
          </v-row>

        </v-container>
      </v-col>
      <v-col cols="12" sm="8" v-if="drill" class="selected-drill" >

            <v-row no-gutters>
                <v-col cols="12" sm="4">
                  <v-btn class='ma-1' @click="save" color="primary">Save</v-btn>
                  <v-btn class='ma-1' @click="deleteDrill" color="error">Delete</v-btn>
                </v-col>
            </v-row>
            <v-row style="height: calc(100vh - 200px); overflow: scroll">
              <v-col cols="12">
                <v-row>
                  <v-col cols="12" sm="6">
                    <v-text-field label="Drill Name" v-model="drill.name" hide-details="auto"></v-text-field>
                  </v-col>
                  <v-col cols="12" sm="4">
                    <v-select clearable chips label="Groups"  v-model="drill.groups" :items="team.groups" multiple hide-details="auto"></v-select>
                  </v-col>
                  <v-col cols="12" sm="2">
                    <v-tooltip text="Add Image">
                      <template v-slot:activator="{ props }">
                        <v-btn class='ma-1' v-bind="props" size="32px" @click="showImageAddDialog(timeSlotIndex, drillIndex)"> <v-icon icon="mdi-image-plus"></v-icon></v-btn>
                      </template>
                    </v-tooltip>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="12">
                    <v-textarea label="Notes" auto-grow
                                rows="1" v-model="drill.notes" hide-details="auto"></v-textarea>
                  </v-col>
                </v-row>

                <v-row >
                  <v-col cols="12" sm="4"  v-for="(image, imageIndex) in drill.images" :key="image">
                    <div style="display:grid">
                      <ImageDisplay :teamId="team.id" :fileName="image" ></ImageDisplay>
                      <v-btn @click="removeDrillImage(imageIndex)">
                        <v-icon color="red" icon="mdi-delete"></v-icon>
                      </v-btn>
                    </div>
                  </v-col>
                </v-row>

              </v-col>

            </v-row>




      </v-col>
    </v-row>


  </v-container>
  <v-dialog
      v-model="showImageAdd"
      width="auto"
      min-width="300"
  >
    <FileUpload v-if="showImageAdd" @uploadComplete="imageUploadComplete" :teamId=team.id></FileUpload>
  </v-dialog>
</template>
    
<script>

import api from '../api'
import FileUpload from "@/components/components/FileUpload.vue";
import ImageDisplay from "@/components/components/ImageDisplay.vue";
import DrillLibraryTree from "@/components/components/DrillLibraryTree.vue";

export default {
  name: 'DrillLibrary',
  components: {FileUpload, ImageDisplay, DrillLibraryTree},
  data() {
    return {
      libraryDrills: [] ,
      filteredLibraryDrills: null,
      drill: null,
      loadingTeams: true,
      error: null,
      teams: [],
      team: null,
      showImageAdd: false,
      searchTerm: null,
      drillTree:[],
      tags: ["Core","Favorites",  "Battle", "Clinic",  "Conditioning","Cradling", "Defense","Dodging", "Footwork", "Give & Go", "Goalie", "Ground Balls", "Passing",
      "Shooting", "Offense", "Penalty Kill", "Scrimmage", "Small Area", "Transition", "Warm Up", "1 vs 1", "2 vs 0","2 vs 1", "2 vs 2", "3 vs 2", "3 vs 3", "4 vs 3", "5 vs 3", "5 vs 4", "5 vs 5"]

    }
  },
  created() {
    this.$watch(
        () => this.team,
        () => {
          this.loadDrills()
        },
        // fetch the data when the view is created and the data is
        // already being observed
        { immediate: true }
    )

    this.$watch(
        () => this.libraryFilter,
        () => {
          console.log("filter update " + this.libraryFilter)
          let newFilterList = [];

          let filter = this.libraryFilter;

          this.libraryDrills.forEach((drill) => {
            if((drill.name && drill.name.indexOf(filter) > -1) || (drill.notes && drill.notes.indexOf(filter) > -1)) {
              newFilterList.push(drill);
            }
          })

          this.filteredLibraryDrills = newFilterList;

          this.setTagTree();
        },
        // fetch the data when the view is created and the data is
        // already being observed
        { immediate: true }
    )

    api.me.get((me, err) => {
      this.loadingTeams = false
      if (err) {
        this.error = err.toString()
      } else {
        this.teams = me.teams

        if(this.teams.length === 1){
          this.team = this.teams[0];
        }
      }

    });
  },
  methods: {
    setTagTree(){
      let filter = this.libraryFilter;

      let untagged = [];
      let tags= {};

      this.libraryDrills.forEach((drill) => {
        if((drill.name && drill.name.indexOf(filter) > -1) || (drill.notes && drill.notes.indexOf(filter) > -1)) {
          if(!drill.tags || drill.tags.length === 0){
            untagged.push(drill)
          } else {
            drill.tags.forEach((tag)=>{
              if(!tags[tag]){
                tags[tag] = []
              }

              tags[tag].push(drill)
            });
          }
        }
      })

      let organizedByTag = [];

      if(untagged.length > 0){
        organizedByTag.push({
          tag: "Untagged",
          drills: untagged
        });
      }

      Object.keys(tags).sort().forEach((key) => {
        organizedByTag.push({
          tag: key,
          drills: tags[key],
        })
      });
      this.drillTree = organizedByTag;

    },
    showImageAddDialog() {
      this.showImageAdd = true
    },

    imageUploadComplete(id) {
      console.log("upload complete: " + id)
      this.showImageAdd = false;

      if(!this.drill.images){
        this.drill.images = []
      }

      this.drill.images.push(id);
    },
    removeDrillImage(imageIndex) {
      this.drill.images.splice(imageIndex, 1);
    },
    selectDrill(drill){
      this.drill = drill;
    },
    save(){
      api.drills.save(this.team.id, this.drill, ()=>{
        this.$toast.open({ message: 'Drill saved', type: 'success' });
        this.drill = null;
        this.loadDrills();
      }), () =>{
        this.$toast.open({ message: 'Unable to save drill', type: 'error' });
      }
    },
    deleteDrill(){
      api.drills.delete(this.team.id, this.drill.id, ()=>{
        this.loadDrills();

        this.drill = null;
      }), () =>{
        this.$toast.open({ message: 'Unable to delete drill', type: 'error' });
      }
    },
    loadDrills(){
      if(this.team){
        let filter = this.libraryFilter;

        api.drills.listForTeam(this.team.id, (drills) =>{
          this.libraryDrills = drills;
          this.filteredLibraryDrills = drills;

          this.libraryFilter = filter;

          this.setTagTree();
        })
      }
    },
  }

}
</script>

<style scoped>

/* Main layout */
.drill-library-main {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  min-height: 100vh;
}

/* Modern card styling */
.modern-card {
  border-radius: 0px !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1) !important;
  transition: all 0.3s ease;
  border: 1px solid rgba(0, 0, 0, 0.08);
}

.modern-card:hover {
  transform: none;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1) !important;
}

.card-header {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  font-weight: 600;
  padding: 16px 24px;
  font-size: 1.1rem;
}

/* Modern drawer styling */
.modern-drawer {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  overflow: hidden !important;
}

.modern-drawer .v-navigation-drawer__content {
  overflow: hidden !important;
}

.drill-library-card {
  display: flex;
  flex-direction: column;
  height: calc(100vh - 300px);
  max-height: calc(100vh - 300px);
}

.drill-library-scroll {
  flex: 1;
  overflow-y: auto;
  padding: 0;
  /* height: calc(100vh - 500px - 60px);
  max-height: calc(100vh - 500px - 60px); */
}

/* Button styling */
.modern-btn {
  border-radius: 0px !important;
  text-transform: none !important;
  font-weight: 500 !important;
  letter-spacing: 0.5px !important;
  transition: all 0.2s ease;
}

.modern-btn:hover {
  transform: none;
}

/* Team display styling */
.team-display {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  background: rgba(0, 0, 0, 0.05);
  border-radius: 0px;
  border: 1px solid rgba(0, 0, 0, 0.1);
}

/* Image card styling */
.image-card {
  border-radius: 0px !important;
  border: 1px solid rgba(0, 0, 0, 0.08);
  transition: all 0.2s ease;
}

.image-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1) !important;
}

/* Action button styling */
.action-btn {
  min-width: 40px !important;
  width: 40px !important;
  height: 40px !important;
  border-radius: 0px !important;
}

/* Text field styling */
.v-text-field .v-field {
  border-radius: 0px !important;
}

/* Select styling */
.v-select .v-field {
  border-radius: 0px !important;
}

/* Textarea styling */
.v-textarea .v-field {
  border-radius: 0px !important;
}

/* Progress bar styling */
.v-progress-linear {
  border-radius: 0px !important;
}

/* Responsive adjustments */
@media (max-width: 960px) {
  .modern-card {
    margin-bottom: 16px;
  }
  
  .card-header {
    padding: 12px 16px;
    font-size: 1rem;
  }
  
  .image-card {
    margin-bottom: 16px;
  }
}

/* Animation for cards */
@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.modern-card {
  animation: slideIn 0.3s ease-out;
}

/* Custom scrollbar for lists */
.v-list {
  scrollbar-width: thin;
  scrollbar-color: rgba(0, 0, 0, 0.2) transparent;
}

.v-list::-webkit-scrollbar {
  width: 6px;
}

.v-list::-webkit-scrollbar-track {
  background: transparent;
}

.v-list::-webkit-scrollbar-thumb {
  background-color: rgba(0, 0, 0, 0.2);
  border-radius: 3px;
}

.v-list::-webkit-scrollbar-thumb:hover {
  background-color: rgba(0, 0, 0, 0.3);
}

/* Legacy styles for compatibility */
.v-expansion-panel-content>>> .v-expansion-panel-content__wrap {
  padding: 0 !important;
}

.v-list-item {
  margin: 0px !important;
  min-height: 0px !important;
  padding-left: 0px !important;
}

.v-list-item>>> .v-list-item-title {
  text-wrap: auto !important;
}

</style>